import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Eye, Tag, ArrowRight, Search } from 'lucide-react';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchPosts();
    fetchTags();
  }, [selectedTag]);

  const fetchPosts = async () => {
    try {
      let url = `${backendUrl}/api/blog`;
      if (selectedTag) {
        url += `?tag=${encodeURIComponent(selectedTag)}`;
      }
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/blog/tags/all`);
      if (res.ok) {
        const data = await res.json();
        setTags(data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-light tracking-wider mb-4">BLOG</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            İpek dünyasından haberler, bakım ipuçları ve stil önerileri
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Blog yazılarında ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 focus:border-black outline-none transition-colors"
            />
          </div>
          
          {/* Tags Filter */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedTag('')}
              className={`px-4 py-2 text-sm transition-colors ${
                !selectedTag ? 'bg-black text-white' : 'bg-white border hover:bg-gray-50'
              }`}
            >
              Tümü
            </button>
            {tags.slice(0, 5).map((t) => (
              <button
                key={t.tag}
                onClick={() => setSelectedTag(t.tag)}
                className={`px-4 py-2 text-sm transition-colors ${
                  selectedTag === t.tag ? 'bg-black text-white' : 'bg-white border hover:bg-gray-50'
                }`}
              >
                {t.tag} ({t.count})
              </button>
            ))}
          </div>
        </div>

        {/* Blog Posts Grid */}
        {filteredPosts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article 
                key={post.post_id} 
                className="bg-white group hover:shadow-lg transition-shadow duration-300"
              >
                {/* Cover Image */}
                <Link to={`/blog/${post.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                    {post.cover_image ? (
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <span className="text-6xl font-light">V</span>
                      </div>
                    )}
                  </div>
                </Link>
                
                {/* Content */}
                <div className="p-6">
                  {/* Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex gap-2 mb-3">
                      {post.tags.slice(0, 2).map((tag) => (
                        <span 
                          key={tag} 
                          className="text-xs text-gray-500 bg-gray-100 px-2 py-1"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Title */}
                  <Link to={`/blog/${post.slug}`}>
                    <h2 className="text-lg font-medium mb-2 group-hover:text-gray-600 transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                  </Link>
                  
                  {/* Excerpt */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{formatDate(post.published_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye size={14} />
                      <span>{post.view_count || 0} görüntüleme</span>
                    </div>
                  </div>
                  
                  {/* Read More */}
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-1 mt-4 text-sm font-medium hover:gap-2 transition-all"
                  >
                    Devamını Oku
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">
              {searchQuery || selectedTag 
                ? 'Arama kriterlerine uygun yazı bulunamadı.' 
                : 'Henüz blog yazısı yok.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
